import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useState } from "react";
import { db, storage } from "../../firebase/FirebaseConfig";
import { Button, IconButton, TextField } from "@mui/material";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { AddCircle, RemoveCircle } from "@mui/icons-material";

type ClubPhotoData = {
  alt: string;
  backRow?: string[];
  frontRow?: string[];
  frontFrontRow?: string[];
  notPictured?: string[];
  photographerName?: string;
  photographerLink?: string;
};

type Props = {
  setAlt: (val: string) => void;
  setBackRow: (val: string[]) => void;
  setFrontRow: (val: string[]) => void;
  setFrontFrontRow: (val: string[]) => void;
  setNotPictured: (val: string[]) => void;
  setPhotographerName: (val: string) => void;
  setPhotographerLink: (val: string) => void;
};

function BulkClubPhotoJsonInput({
  setAlt,
  setBackRow,
  setFrontRow,
  setFrontFrontRow,
  setNotPictured,
  setPhotographerName,
  setPhotographerLink,
}: Props) {
  const [jsonInput, setJsonInput] = useState("");
  const [error, setError] = useState("");

  const handleApplyJson = () => {
    try {
      const parsed: ClubPhotoData = JSON.parse(jsonInput);
      console.log("Parsed JSON:", parsed);
      console.log("Setting backRow to:", parsed.backRow);

      if (!parsed.alt) throw new Error("Missing required field: 'alt'");

      setAlt(parsed.alt);
      setBackRow(parsed.backRow ?? []);
      setFrontRow(parsed.frontRow ?? []);
      setFrontFrontRow(parsed.frontFrontRow ?? []);
      setNotPictured(parsed.notPictured ?? []);
      setPhotographerName(parsed.photographerName ?? "");
      setPhotographerLink(parsed.photographerLink ?? "");

      setError("");
    } catch (e: any) {
      setError(e.message || "Invalid JSON format");
    }
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <h4>Paste Club Photo JSON</h4>
      <TextField
        label="JSON Input"
        multiline
        rows={10}
        fullWidth
        value={jsonInput}
        onChange={(e) => setJsonInput(e.target.value)}
        error={!!error}
        helperText={error || "Paste JSON for alt, rows, photographer info"}
        variant="outlined"
      />
      <Button
        variant="contained"
        color="primary"
        onClick={handleApplyJson}
        style={{ marginTop: 12 }}
      >
        Apply JSON
      </Button>
    </div>
  );
}

type StringListInputProps = {
  label: string;
  values: string[];
  setValues: (val: string[]) => void;
};

export function StringListInput({
  label,
  values,
  setValues,
}: StringListInputProps) {
  const handleChange = (index: number, newValue: string) => {
    const updated = [...values];
    updated[index] = newValue;
    setValues(updated);
  };

  const handleAdd = () => setValues([...values, ""]);
  const handleRemove = (index: number) => {
    const updated = values.filter((_, i) => i !== index);
    setValues(updated);
  };

  return (
    <div style={{ marginBottom: "1rem" }}>
      <h4>{label}</h4>
      {values.map((val, idx) => (
        <div
          key={idx}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 8,
          }}
        >
          <TextField
            label={`${label} #${idx + 1}`}
            value={val}
            onChange={(e) => handleChange(idx, e.target.value)}
            size="small"
          />
          <IconButton
            onClick={() => handleRemove(idx)}
            disabled={values.length === 1}
          >
            <RemoveCircle color="error" />
          </IconButton>
        </div>
      ))}
      <IconButton onClick={handleAdd}>
        <AddCircle color="primary" />
      </IconButton>
    </div>
  );
}

export default function AddNewClubPhoto() {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [alt, setAlt] = useState("");
  const [quarter, setQuarter] = useState("");
  const [backRow, setBackRow] = useState([""]);
  const [frontRow, setFrontRow] = useState([""]);
  const [frontFrontRow, setFrontFrontRow] = useState([""]);
  const [notPictured, setNotPictured] = useState([""]);
  const [photographerLink, setPhotographerLink] = useState("");
  const [photographerName, setPhotographerName] = useState("");
  const [loadingMessage, setLoadingMessage] = useState("");

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      // Remove file extension and set as alt text
      const fileName = file.name;
      const altText =
        fileName.substring(0, fileName.lastIndexOf(".")) || fileName;
      setAlt(altText);
    }
  };

  const handleAddClubPhoto = async () => {
    if (!imageFile) {
      alert("Please select an image");
      return;
    }

    if (!alt) {
      alert("Please provide alt text");
      return;
    }

    try {
      const imagePath = `ClubPhotos/${imageFile.name}`;
      const storageRef = ref(storage, imagePath);

      console.log("Uploading image...");
      setLoadingMessage("Uploading image...");
      await uploadBytes(storageRef, imageFile);
      const previewUrl = await getDownloadURL(storageRef);
      console.log("Image uploaded, URL:", previewUrl);

      const newPhoto = {
        alt,
        photoURL: imagePath,
        quarter,
        backRow,
        frontRow,
        frontFrontRow,
        notPictured,
        photographerLink,
        photographerName,
      };

      const docRef = doc(db, "clubPhoto", "clubPic");
      const snapshot = await getDoc(docRef);

      console.log("Fetching existing data...");
      const existingData = snapshot.exists() ? snapshot.data() : {};
      const existingPhotos = Array.isArray(existingData?.pics)
        ? existingData.pics
        : [];

      console.log("Existing photos count:", existingPhotos.length);
      const updatedPhotos = [...existingPhotos, newPhoto];

      await updateDoc(docRef, {
        pics: updatedPhotos,
      });

      console.log("Firestore updated");
      setLoadingMessage("Image uploaded");

      // Reset form
      setImageFile(null);
      setAlt("");
      setQuarter("");
      setBackRow([""]);
      setFrontRow([""]);
      setFrontFrontRow([""]);
      setNotPictured([""]);
      setPhotographerLink("");
      setPhotographerName("");

      alert("Club photo uploaded successfully!");
    } catch (error) {
      console.error("Error uploading club photo:", error);
      alert("Failed to upload photo. Check console for details.");
    }
    setLoadingMessage("");
  };

  return (
    <div className="faq">
      {/* <BulkClubPhotoJsonInput
        setAlt={setAlt}
        setBackRow={setBackRow}
        setFrontRow={setFrontRow}
        setFrontFrontRow={setFrontFrontRow}
        setNotPictured={setNotPictured}
        setPhotographerName={setPhotographerName}
        setPhotographerLink={setPhotographerLink}
      /> */}

      <div style={{ textAlign: "center" }}>
        <h1>ADD NEW CLUB PHOTO</h1>
        <h4>
          Wait a second after submitting, you'll get an alert confirming that it
          has been uploaded.
        </h4>
      </div>

      <TextField
        label="Quarter"
        value={quarter}
        onChange={(e) => setQuarter(e.target.value)}
        size="small"
        style={{ paddingBottom: 20 }}
      />

      <TextField
        label="Alt Text (auto-filled from filename)"
        value={alt}
        onChange={(e) => setAlt(e.target.value)}
        size="small"
        style={{ paddingBottom: 20 }}
        fullWidth
      />

      <TextField
        label="Photographer Link"
        value={photographerLink}
        onChange={(e) => setPhotographerLink(e.target.value)}
        size="small"
        style={{ paddingBottom: 20 }}
      />

      <TextField
        label="Photographer Name"
        value={photographerName}
        onChange={(e) => setPhotographerName(e.target.value)}
        size="small"
        style={{ paddingBottom: 20 }}
      />

      <input
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ marginBottom: 20 }}
      />

      <StringListInput
        label="Back Row"
        values={backRow}
        setValues={setBackRow}
      />
      <StringListInput
        label="Front Row"
        values={frontRow}
        setValues={setFrontRow}
      />
      <StringListInput
        label="Front-Front Row"
        values={frontFrontRow}
        setValues={setFrontFrontRow}
      />
      <StringListInput
        label="Not Pictured"
        values={notPictured}
        setValues={setNotPictured}
      />

      <Button
        onClick={handleAddClubPhoto}
        size="small"
        variant="contained"
        color="primary"
      >
        Upload Club Photo Data
      </Button>
      <h3>{loadingMessage}</h3>
    </div>
  );
}
